'use client'

import { useEffect, useRef } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { trackPageview } from './track'
import { isAnalyticsExcludedPath } from './excluded-paths'

/**
 * Reports client-side route changes as pageviews. The App Router does NOT emit
 * pageviews on soft navigations, so SPA route changes would otherwise be
 * invisible to GA/Pixel.
 *
 * The initial pageview is fired by each platform's own init snippet (see
 * AnalyticsScripts), so this skips the very first render and reports only
 * subsequent navigations — guaranteeing exactly one pageview per page.
 *
 * Uses `useSearchParams`, so callers MUST wrap this in a <Suspense> boundary
 * (Next 15 requirement). trackPageview itself no-ops when analytics is
 * unconfigured, so this is inert in dev / previews.
 */
export function PageviewTracker() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  // Key the effect off the serialized query, not the searchParams object —
  // useSearchParams() is not guaranteed to be referentially stable across
  // re-renders, so depending on the object could re-fire a pageview for the
  // same URL on an unrelated render. (Codex review.)
  const query = searchParams?.toString() ?? ''
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    if (!pathname || isAnalyticsExcludedPath(pathname, query)) return
    trackPageview(query ? `${pathname}?${query}` : pathname)
  }, [pathname, query])

  return null
}
